import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EMAIL_FUNCTIONAL } from "@/lib/early-access";

export const Route = createFileRoute("/admin-login")({
  head: () => ({
    meta: [
      { title: "Admin Login · Smartasaurus" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success("Signed in");
      navigate({ to: "/admin", search: { section: "overview" } });
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8">
        <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
          ← Back to site
        </Link>
        <h1 className="mt-4 font-display text-3xl uppercase">Admin Login</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Internal Smartasaurus administration.
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={{ fontSize: "16px" }} />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} style={{ fontSize: "16px" }} />
          </div>
          <Button type="submit" disabled={loading} className="w-full">
            {loading ? "..." : "Sign in"}
          </Button>
        </form>
        {EMAIL_FUNCTIONAL && (
          <div className="mt-4">
            <button
              type="button"
              disabled={loading}
              onClick={async () => {
                if (!email) { toast.error("Enter your email first"); return; }
                setLoading(true);
                try {
                  const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: window.location.origin + "/reset-password",
                  });
                  if (error) throw error;
                  toast.success("Password reset email sent. Check your inbox.");
                } catch (err: any) {
                  toast.error(err.message ?? "Could not send reset email");
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full text-xs uppercase tracking-widest text-muted-foreground hover:text-primary disabled:opacity-50"
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
